import type { ProfileStore } from './ProfileStore';
import { DEFAULT_CHARACTER, isCharacterId, type CharacterId } from '../characters/CharacterDefs';
import { META_UPGRADES_BY_ID, metaUpgradeRank, type MetaUpgradeId } from './metaUpgrades';

/**
 * The persistent player profile (design §4.5/§4.7, revised this session):
 * - collection maps artifactId → best banked QUALITY roll (0.80–1.20).
 *   Banking happens at each descent checkpoint and on wins; death loses only
 *   the current floor's unbanked finds.
 * - shards come from banking duplicates/worse rolls AND the run-end gem tithe
 *   (economy.ts gemShardYield); spent on quality upgrades toward the 1.20 cap
 *   and the capped meta upgrade tracks (metaUpgrades.ts). No crafting.
 * - milestone unlocks are smoothing; purchased upgrade tracks may be capped
 *   power (revised pillar, July 2026).
 */

/** A per-tier best-clear record. */
export interface LadderBest {
  seconds: number;
  /** CharacterId of the record run ('' on migrated legacy records). */
  character: string;
  /** Kill count of the record run (0 on migrated legacy records). */
  kills: number;
}

export interface ProfileData {
  version: 1;
  /** artifactId → best banked quality (0.80–1.20). Ownership = discovery. */
  collection: Record<string, number>;
  shards: number;
  unlocks: {
    artifactSlot1: boolean; // carry 5 → 6
    artifactSlot2: boolean; // 6 → 7
    guidedPicks: boolean; // early-offer forgiveness extends to pick 3
    startingMend: boolean; // pocket pre-stocked with a Mend
    charVane: boolean;
    charThirl: boolean;
    charCask: boolean;
    charTrine: boolean;
    charMourne: boolean;
    charBramGaunt: boolean;
  };
  stats: {
    wins: number;
    deaths: number;
    bestFloor: number;
    runsStarted: number;
    /** Lifetime counters (Depths rework unlock gates). */
    totalKills: number;
    totalGems: number;
  };
  /** Purchased meta upgrade ranks: trackId → rank (metaUpgrades.ts). */
  upgrades: Record<string, number>;
  /**
   * Difficulty ladder records: tier (decimal-string key) → best clear.
   * Legacy saves stored a bare seconds number — migrated on load with
   * character ''/kills 0 (rendered as unknown).
   */
  ladder: { bests: Record<string, LadderBest> };
  /** Sticky pre-run tier pick; clamped to maxUnlockedTier at use time. */
  lastTier: number;
  /** Remembered bring-2 selection; validated against the collection on use. */
  lastLoadout: string[];
  /** Remembered character pick; validated via isCharacterId on use. */
  lastCharacter: string;
}

export function defaultProfile(): ProfileData {
  return {
    version: 1,
    collection: {},
    shards: 0,
    unlocks: {
      artifactSlot1: false, artifactSlot2: false, guidedPicks: false, startingMend: false,
      charVane: false, charThirl: false, charCask: false,
      charTrine: false, charMourne: false, charBramGaunt: false,
    },
    stats: { wins: 0, deaths: 0, bestFloor: 0, runsStarted: 0, totalKills: 0, totalGems: 0 },
    upgrades: {},
    ladder: { bests: {} },
    lastTier: 0,
    lastLoadout: [],
    lastCharacter: DEFAULT_CHARACTER,
  };
}

/**
 * Pre-rework (foresight/refusal) ranks are refunded as shards rather than
 * silently dropped — the tracks no longer exist. Cumulative spend at each
 * rank, from the retired cost tables. Idempotent: the legacy ids are never
 * re-written, so a refund can fire at most once per stored blob.
 */
function legacyTrackRefund(raw: unknown): number {
  if (typeof raw !== 'object' || raw === null) return 0;
  const refunds: Record<string, readonly number[]> = {
    foresight: [70, 220], // 70, 70+150
    refusal: [50, 160], // 50, 50+110
  };
  let total = 0;
  for (const [id, table] of Object.entries(refunds)) {
    const v = (raw as Record<string, unknown>)[id];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    const rank = Math.min(table.length, Math.max(0, Math.floor(v)));
    if (rank > 0) total += table[rank - 1]!;
  }
  return total;
}

/** Keep only the CURRENT unlock keys — stale ones (reroll, old char gates,
 *  cut characters' charVigil/charMagpie) are dropped; the retired `reroll`
 *  maps onto guidedPicks so first-win veterans keep something. A stored
 *  lastCharacter of a cut id falls back via isCharacterId the same way. */
function sanitizeUnlocks(raw: unknown, base: ProfileData['unlocks']): ProfileData['unlocks'] {
  const out = { ...base };
  if (typeof raw !== 'object' || raw === null) return out;
  const rec = raw as Record<string, unknown>;
  for (const key of Object.keys(base) as (keyof ProfileData['unlocks'])[]) {
    if (typeof rec[key] === 'boolean') out[key] = rec[key] as boolean;
  }
  if (rec['reroll'] === true) out.guidedPicks = true;
  return out;
}

function sanitizeUpgrades(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (typeof raw !== 'object' || raw === null) return out;
  for (const [id, def] of META_UPGRADES_BY_ID) {
    const v = (raw as Record<string, unknown>)[id];
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    const rank = Math.min(def.maxRank, Math.max(0, Math.floor(v)));
    if (rank > 0) out[id] = rank;
  }
  return out;
}

function sanitizeLadder(raw: unknown): { bests: Record<string, LadderBest> } {
  const bests: Record<string, LadderBest> = {};
  const rawBests = typeof raw === 'object' && raw !== null
    ? (raw as { bests?: unknown }).bests
    : undefined;
  if (typeof rawBests === 'object' && rawBests !== null) {
    for (const [key, v] of Object.entries(rawBests as Record<string, unknown>)) {
      if (!/^\d+$/.test(key)) continue;
      // Legacy shape: a bare seconds number.
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
        bests[key] = { seconds: v, character: '', kills: 0 };
        continue;
      }
      if (typeof v === 'object' && v !== null) {
        const rec = v as Partial<LadderBest>;
        if (typeof rec.seconds !== 'number' || !Number.isFinite(rec.seconds) || rec.seconds <= 0) {
          continue;
        }
        bests[key] = {
          seconds: rec.seconds,
          character: typeof rec.character === 'string' ? rec.character : '',
          kills: typeof rec.kills === 'number' && Number.isFinite(rec.kills)
            ? Math.max(0, Math.floor(rec.kills))
            : 0,
        };
      }
    }
  }
  return { bests };
}

export function deserializeProfile(raw: string | null): ProfileData {
  if (raw === null) return defaultProfile();
  try {
    const parsed = JSON.parse(raw) as Partial<ProfileData>;
    if (parsed.version !== 1) return defaultProfile();
    const base = defaultProfile();
    const shards = typeof parsed.shards === 'number' && parsed.shards >= 0 ? parsed.shards : 0;
    const data: ProfileData = {
      version: 1,
      collection: typeof parsed.collection === 'object' && parsed.collection !== null
        ? { ...parsed.collection }
        : base.collection,
      // Legacy foresight/refusal spend comes back as shards (see refund fn).
      shards: shards + legacyTrackRefund(parsed.upgrades),
      unlocks: sanitizeUnlocks(parsed.unlocks, base.unlocks),
      stats: { ...base.stats, ...(parsed.stats ?? {}) },
      // Pre-upgrades profiles lack this field; sanitize rather than spread —
      // keep known track ids only, floor, clamp to [0, maxRank].
      upgrades: sanitizeUpgrades(parsed.upgrades),
      ladder: sanitizeLadder(parsed.ladder),
      lastTier: typeof parsed.lastTier === 'number' && Number.isFinite(parsed.lastTier)
        ? Math.max(0, Math.floor(parsed.lastTier))
        : 0,
      lastLoadout: Array.isArray(parsed.lastLoadout) ? parsed.lastLoadout.slice() : [],
      // Pre-character profiles lack this field — default, don't reset.
      lastCharacter: isCharacterId(parsed.lastCharacter) ? parsed.lastCharacter : DEFAULT_CHARACTER,
    };
    // Loadout must reference owned artifacts, at most 2.
    data.lastLoadout = data.lastLoadout
      .filter((id) => typeof id === 'string' && data.collection[id] !== undefined)
      .slice(0, 2);
    return data;
  } catch {
    return defaultProfile();
  }
}

export class Profile {
  readonly data: ProfileData;

  constructor(private readonly store: ProfileStore) {
    this.data = deserializeProfile(store.load());
  }

  save(): void {
    this.store.save(JSON.stringify(this.data));
  }

  ownedQuality(artifactId: string): number | undefined {
    return this.data.collection[artifactId];
  }

  get ownedCount(): number {
    return Object.keys(this.data.collection).length;
  }

  get artifactCapacity(): number {
    return 5 + (this.data.unlocks.artifactSlot1 ? 1 : 0) + (this.data.unlocks.artifactSlot2 ? 1 : 0);
  }

  upgradeRank(id: MetaUpgradeId): number {
    return metaUpgradeRank(this.data, id);
  }

  /** Loadout validated against current ownership, ≤2 entries. */
  get loadout(): string[] {
    return this.data.lastLoadout
      .filter((id) => this.data.collection[id] !== undefined)
      .slice(0, 2);
  }

  /** Validated character pick (falls back to the default character). */
  get lastCharacter(): CharacterId {
    const v = this.data.lastCharacter;
    return isCharacterId(v) ? v : DEFAULT_CHARACTER;
  }

  setLastCharacter(id: CharacterId): void {
    this.data.lastCharacter = id;
    this.save();
  }

  /** Sticky ladder pick (clamp against maxUnlockedTier happens at use time). */
  setLastTier(tier: number): void {
    this.data.lastTier = Math.max(0, Math.floor(tier));
    this.save();
  }

  setLastLoadout(ids: string[]): void {
    this.data.lastLoadout = ids
      .filter((id) => this.data.collection[id] !== undefined)
      .slice(0, 2);
    this.save();
  }

  recordRunStart(): void {
    this.data.stats.runsStarted++;
    this.save();
  }
}
